import { lazy as dynamic, Suspense, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useSnapshot } from 'valtio/react';

import {
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
} from '@paid-ui/components/drawer';
import { LoadingIndicator } from '@paid-ui/components/loading-indicator';
import { ClaimAction, ClaimMethod, ClaimType } from '@paid-ui/enums/claim';
import { ContractActionType, ContractRole, ContractTemplate } from '@paid-ui/enums/contract';
import { VariationActionType } from '@paid-ui/enums/variation';
import TimelineDetails from '@paid-ui/forms/timeline-details';

import { closeFlyout, dismissFlyout, getFlyoutEscapeKeyDownHandler, openFlyout } from './actions';
import {
  EDIT_PARTY_SIGNATORIES_DIALOG,
  flyoutManager,
  FlyoutType,
  programmaticOnlyFlyouts,
} from './model';

const AccessLinkedContractsRequestForm = dynamic(
  () => import('@paid-ui/forms/access-linked-contracts'),
);
const ApplyAccountForm = dynamic(() => import('@paid-ui/forms/apply-account'));
const PaymentAccountForm = dynamic(() => import('@paid-ui/forms/payment-account'));
const KycOnlyForm = dynamic(() => import('@paid-ui/forms/kyc-only'));
const ManagePayoutAccountForm = dynamic(() => import('@paid-ui/forms/manage-payout-account'));
const AccountTransfer = dynamic(() => import('@paid-ui/forms/account-transfer'));
const AccountTransferIntra = dynamic(() => import('@paid-ui/forms/account-transfer-intra'));
const AssignHeldFundsForm = dynamic(() => import('@paid-ui/forms/assign-held-funds'));
const TransactionDetails = dynamic(() => import('@paid-ui/blocks/transaction-details'));
const TransactionHistoryFlyout = dynamic(
  () => import('@paid-ui/blocks/transaction-history-flyout'),
);
const CreateProjectForm = dynamic(() => import('@paid-ui/forms/create-project'));
const EditProjectForm = dynamic(() => import('@paid-ui/forms/edit-project'));
const UpdateProjectStatus = dynamic(() => import('@paid-ui/forms/update-project-status'));
const DeleteProject = dynamic(() => import('@paid-ui/forms/delete-project'));
const JoinContractForm = dynamic(() => import('@paid-ui/forms/join-contract'));
const PrimeContractForm = dynamic(() => import('@paid-ui/forms/prime-contract'));
const SignContract = dynamic(() => import('@paid-ui/blocks/sign-contract'));
const AbandonContract = dynamic(() => import('@paid-ui/blocks/abandon/AbandonContract'));
const DeleteDraftContract = dynamic(() => import('@paid-ui/blocks/delete-draft-contract'));
const RevokeContractForm = dynamic(() => import('@paid-ui/forms/revoke-contract'));
const RecallContractForm = dynamic(() => import('@paid-ui/forms/recall-contract'));
const RetireUserGroupForm = dynamic(() => import('@paid-ui/forms/retire-user-group'));
const UnjoinContractConfirm = dynamic(() => import('@paid-ui/forms/unjoin-contract'));
const AbandonPaymentRequestForm = dynamic(() => import('@paid-ui/forms/abandon-payment-request'));
const InviteByEmailForm = dynamic(() => import('@paid-ui/forms/invite-by-email'));
const InviteByLinkForm = dynamic(() => import('@paid-ui/forms/invite-by-link'));
const ContractDetails = dynamic(() => import('@paid-ui/blocks/contract-details'));
const UploadSignedContractForm = dynamic(() => import('@paid-ui/forms/upload-signed-contract'));
const ContractNavigator = dynamic(() => import('@paid-ui/blocks/contract-navigator'));
const SetCommencementDateForm = dynamic(() => import('@paid-ui/forms/set-commencement-date'));
const SetCompletionDateForm = dynamic(() => import('@paid-ui/forms/set-completion-date'));
const LinkedPayments = dynamic(() => import('@paid-ui/blocks/linked-payments'));
const PaymentRequestDetails = dynamic(() => import('@paid-ui/blocks/payment-request-details'));
const ContractForms = dynamic(() => import('@paid-ui/blocks/contract-forms'));
const ProjectForms = dynamic(() => import('@paid-ui/blocks/project-forms'));
const PaymentDetails = dynamic(() => import('@paid-ui/blocks/payment-details'));
const UpdateConsolidatedClaimForm = dynamic(
  () => import('@paid-ui/forms/update-consolidated-claim'),
);
const ChangePrimaryContactForm = dynamic(() => import('@paid-ui/forms/change-primary-contact'));
const UpdateFeeStructureForm = dynamic(() => import('@paid-ui/forms/update-fee-structure'));
const PayClaimForm = dynamic(() => import('@paid-ui/forms/pay-claim'));
const ClaimDetails = dynamic(() => import('@paid-ui/blocks/claim-details'));
const PeriodicVariationForm = dynamic(() => import('@paid-ui/forms/periodic-variation'));
const AbandonVariation = dynamic(() => import('@paid-ui/blocks/abandon/AbandonVariation'));
const VariationDetails = dynamic(() => import('@paid-ui/blocks/variation-details'));
const AbandonAdjustment = dynamic(() => import('@paid-ui/blocks/abandon/AbandonAdjustment'));
const AdjustmentDetails = dynamic(() => import('@paid-ui/blocks/adjustment-details'));

const SecurePayment = dynamic(() => import('@paid-ui/blocks/secure-funds/secure-payment'));
const SecureVariation = dynamic(() => import('@paid-ui/blocks/secure-funds/secure-variation'));
const EarlyReleaseForm = dynamic(() => import('@paid-ui/forms/early-release'));

const SecurePaid = dynamic(() => import('@paid-ui/forms/secure-paid'));
const UnsecurePaid = dynamic(() => import('@paid-ui/forms/unsecure-paid'));
const RequestAssessmentForm = dynamic(() => import('@paid-ui/forms/request-assessment'));
const ClaimAssessmentForm = dynamic(() => import('@paid-ui/forms/claim-assessment'));
const UnsharedDrafts = dynamic(() => import('@paid-ui/forms/unshared-drafts'));
const HelpCenterForm = dynamic(() => import('@paid-ui/forms/help-center'));
const CheckoutForm = dynamic(() => import('@paid-ui/forms/checkout'));
const NewLinkingForm = dynamic(() => import('@paid-ui/blocks/new-linking'));
const SupercontractForm = dynamic(() => import('@paid-ui/forms/supercontract'));
const ProjectParties = dynamic(() => import('@paid-ui/blocks/project-parties/flyout'));
const PartyContractsFlyout = dynamic(
  () => import('@paid-ui/blocks/project-parties/components/flyouts/party-contracts'),
);
const SuperclaimForm = dynamic(() => import('@paid-ui/forms/superclaim'));
const EditPersonalForm = dynamic(() => import('@paid-ui/forms/edit-personal'));
const EditBusinessForm = dynamic(() => import('@paid-ui/forms/edit-business'));
const EditTrusteesForm = dynamic(() => import('@paid-ui/forms/edit-trustees'));
const EditIndividualTrustees = dynamic(() => import('@paid-ui/forms/edit-individual-trustees'));
const EditSignatoryForm = dynamic(() => import('@paid-ui/forms/edit-signatory'));
const EditPermissionsForm = dynamic(() => import('@paid-ui/forms/edit-permissions'));
const InvitePartyForm = dynamic(() => import('@paid-ui/forms/invite-party'));
const InviteUserForm = dynamic(() => import('@paid-ui/forms/invite-user'));
const EditDirectorStructureForm = dynamic(() => import('@paid-ui/forms/edit-director-structure'));
const SetDefaultSignatoriesForm = dynamic(() => import('@paid-ui/forms/set-default-signatories'));
const EditPartySignatoriesForm = dynamic(() => import('@paid-ui/forms/edit-party-signatories'));
const ProjectSelectForm = dynamic(() => import('@paid-ui/forms/project-select'));
const SubmitClaimForm = dynamic(() => import('@paid-ui/forms/submit-claim'));

const AccountDetails = dynamic(() => import('@paid-ui/blocks/account-details'));
const DepositRefundForm = dynamic(() => import('@paid-ui/forms/deposit-refund'));
const TerminateContractForm = dynamic(() => import('@paid-ui/forms/terminate-contract'));

const ProjectFilters = dynamic(() => import('@paid-ui/blocks/project-filters'));
const ProjectContractsFlyout = dynamic(
  () => import('@paid-ui/blocks/project-contracts/components/project-contracts-flyout'),
);
const ProjectContractsFilters = dynamic(
  () => import('@paid-ui/blocks/project-contracts/components/extra-table-filters'),
);
const BulkPaymentForm = dynamic(
  () => import('@paid-ui/blocks/project-claims/components/flyouts/bulk-payment'),
);
const ContractFilters = dynamic(() => import('@paid-ui/blocks/contract-filters'));
const ClaimFilters = dynamic(() => import('@paid-ui/blocks/claim-filters'));
const ProjectClaimsFilters = dynamic(
  () => import('@paid-ui/blocks/project-claims/components/flyouts/table-filters'),
);

const NotYetSupported = dynamic(() => import('@paid-ui/forms/not-yet-supported'));

const RecallClaimForm = dynamic(() => import('@paid-ui/forms/recall-claim'));
const UpdateClaimAmountForm = dynamic(() => import('@paid-ui/forms/update-claim-amount'));
const FundingDetails = dynamic(() => import('@paid-ui/blocks/funding-details'));
const ProjectBudget = dynamic(() => import('@paid-ui/blocks/project-budget'));
const ProjectDrawnToDate = dynamic(() => import('@paid-ui/blocks/project-drawn-to-date'));
const ProjectCostToComplete = dynamic(() => import('@paid-ui/blocks/project-cost-to-complete'));
const ProjectDrawdowns = dynamic(() => import('@paid-ui/blocks/project-drawdowns'));
const ProjectLoanAdjustments = dynamic(() => import('@paid-ui/blocks/project-loan-adjustments'));
const EditProjectBudgetForm = dynamic(() => import('@paid-ui/forms/edit-project-budget'));

const SubmitRetentionReturnForm = dynamic(() => import('@paid-ui/forms/submit-retention-return'));
const ReviewRetentionReturnForm = dynamic(() => import('@paid-ui/forms/review-retention-return'));
const RetentionReturnHistory = dynamic(() => import('@paid-ui/blocks/retention-return-history'));
const RetentionReturnDetails = dynamic(() => import('@paid-ui/blocks/retention-return-details'));
const ContractRequiredActionsFlyout = dynamic(
  () => import('@paid-ui/blocks/contract-required-actions/flyout'),
);
const ContractQSReportsFlyout = dynamic(() => import('@paid-ui/blocks/contract-qs-reports/flyout'));
const ContractDocumentsFlyout = dynamic(() => import('@paid-ui/blocks/contract-documents/flyout'));
const ContractPaymentsFlyout = dynamic(
  () => import('@paid-ui/blocks/contract-claims/contract-payments-flyout'),
);
const ContractVariationsFlyout = dynamic(
  () => import('@paid-ui/blocks/contract-variations/contract-variations-flyout'),
);
const ContractRetentionFlyout = dynamic(
  () => import('@paid-ui/blocks/contract-retention/contract-retention-flyout'),
);

interface Props {
  type: FlyoutType;
  details: Record<string, any>;
}

const upper = (qs: string | string[] | undefined) => {
  const q = Array.isArray(qs) ? qs[0] : qs;
  return q ? q.toUpperCase() : '';
};

const preventDefault = (event: Event) => event.preventDefault();

const DefaultGlobalFlyoutContent: React.FC<{ type: FlyoutType }> = ({ type }) => {
  switch (type) {
    case FlyoutType.EDIT_PERSONAL_INFO: {
      return <EditPersonalForm />;
    }
    case FlyoutType.EDIT_BUSINESS_INFO: {
      return <EditBusinessForm />;
    }
    case FlyoutType.EDIT_TRUSTEES_INFO: {
      return <EditTrusteesForm />;
    }
    case FlyoutType.EDIT_INDIVIDUAL_TRUSTEES: {
      return <EditIndividualTrustees />;
    }
    case FlyoutType.APPLY_ACCOUNT: {
      return <ApplyAccountForm />;
    }
    case FlyoutType.KYC_ONLY: {
      return <KycOnlyForm />;
    }
    case FlyoutType.CREATE_PROJECT: {
      return <CreateProjectForm />;
    }
    case FlyoutType.EDIT_PROJECT: {
      return <EditProjectForm />;
    }
    case FlyoutType.WORK_ITEM_DETAILS: {
      return <NotYetSupported />;
    }
    case FlyoutType.HELP_CENTER: {
      return <HelpCenterForm />;
    }
    case FlyoutType.ACCOUNT_TRANSFER: {
      return <AccountTransfer />;
    }
    case FlyoutType.ACCOUNT_TRANSFER_INTRA: {
      return <AccountTransferIntra />;
    }
    case FlyoutType.ABANDON_CONTRACT: {
      return <AbandonContract />;
    }
    case FlyoutType.DELETE_DRAFT_CONTRACT: {
      return <DeleteDraftContract />;
    }
    case FlyoutType.ABANDON_VARIATION: {
      return <AbandonVariation />;
    }
    case FlyoutType.ABANDON_ADJUSTMENT: {
      return <AbandonAdjustment />;
    }
    case FlyoutType.ARCHIVE_PROJECT: {
      return <UpdateProjectStatus />;
    }
    case FlyoutType.DELETE_PROJECT: {
      return <DeleteProject />;
    }
    case FlyoutType.SET_COMMENCEMENT_DATE: {
      return <SetCommencementDateForm />;
    }
    case FlyoutType.TIMELINE_DETAILS: {
      return <TimelineDetails />;
    }
    case FlyoutType.SET_COMPLETION_DATE: {
      return <SetCompletionDateForm />;
    }
    case FlyoutType.NEW_LINKING: {
      return <NewLinkingForm />;
    }
    case FlyoutType.CONTRACT_FILTERS: {
      return <ContractFilters />;
    }
    case FlyoutType.CLAIM_FILTERS: {
      return <ClaimFilters />;
    }
    case FlyoutType.PROJECT_FILTERS: {
      return <ProjectFilters />;
    }
    case FlyoutType.UNSHARED_DRAFTS: {
      return <UnsharedDrafts />;
    }
    default: {
      return null;
    }
  }
};

const GlobalFlyoutContent: React.FC<Props> = ({ type, details }) => {
  switch (type) {
    case FlyoutType.PROJECT_PARTIES: {
      return <ProjectParties />;
    }
    case FlyoutType.PROJECT_CONTRACTS: {
      return <ProjectContractsFlyout />;
    }
    case FlyoutType.PROJECT_CONTRACT_FILTERS: {
      const { table } = details ?? {};
      return <ProjectContractsFilters table={table} />;
    }
    case FlyoutType.PROJECT_CLAIMS_FILTERS: {
      return <ProjectClaimsFilters />;
    }
    case FlyoutType.BULK_PAYMENT: {
      const { table } = details ?? {};
      return <BulkPaymentForm table={table} />;
    }
    case FlyoutType.PARTY_CONTRACTS: {
      return <PartyContractsFlyout />;
    }
    case FlyoutType.EDIT_SIGNATORY: {
      return <EditSignatoryForm />;
    }
    case FlyoutType.INVITE_USER: {
      const { userGroupId } = details ?? {};
      return <InviteUserForm userGroupId={userGroupId} />;
    }
    case FlyoutType.EDIT_DIRECTOR_STRUCTURE: {
      const { userGroupId, businessId } = details ?? {};
      return <EditDirectorStructureForm userGroupId={userGroupId} businessId={businessId} />;
    }
    case FlyoutType.SET_DEFAULT_SIGNATORIES: {
      const { userGroupId } = details ?? {};
      return <SetDefaultSignatoriesForm userGroupId={userGroupId} />;
    }
    case FlyoutType.EDIT_PARTY_SIGNATORIES: {
      const { contractId, partyId } = details ?? {};
      return <EditPartySignatoriesForm key={partyId} contractId={contractId} partyId={partyId} />;
    }
    case FlyoutType.INVITE_PARTY: {
      const { partyId, role } = details ?? {};
      return <InvitePartyForm partyId={partyId} role={role} />;
    }
    case FlyoutType.EDIT_PERMISSIONS: {
      const { userGroupId, userId } = details ?? {};
      return <EditPermissionsForm userGroupId={userGroupId} userId={userId} />;
    }
    case FlyoutType.TRANSACTION_DETAILS: {
      const { matcherEntryNumber, subTab = 'overview' } = details ?? {};
      return <TransactionDetails matcherEntryNumber={matcherEntryNumber} defaultTab={subTab} />;
    }
    case FlyoutType.TRANSACTION_HISTORY: {
      const { accountId } = details ?? {};
      return <TransactionHistoryFlyout accountId={accountId} />;
    }
    case FlyoutType.PAYMENT_ACCOUNT: {
      return <PaymentAccountForm />;
    }
    case FlyoutType.MANAGE_PAYOUT_ACCOUNT: {
      const { externalBankAccountId } = details ?? {};
      return <ManagePayoutAccountForm externalBankAccountId={externalBankAccountId} />;
    }
    case FlyoutType.FUNDING_DETAILS: {
      return <FundingDetails />;
    }
    case FlyoutType.REVOKE_CONTRACT: {
      const { contractId } = details ?? {};
      return <RevokeContractForm contractId={contractId} />;
    }
    case FlyoutType.RECALL_CONTRACT: {
      const { contractId } = details ?? {};
      return <RecallContractForm contractId={contractId} />;
    }
    case FlyoutType.RETIRE_USER_GROUP: {
      const { userGroupId } = details ?? {};
      return <RetireUserGroupForm userGroupId={userGroupId} />;
    }
    case FlyoutType.UNJOIN_CONTRACT: {
      const { contractId } = details ?? {};
      return <UnjoinContractConfirm contractId={contractId} />;
    }
    case FlyoutType.SUPER_CONTRACT: {
      const { contractId, isDraft, isReview, contractType } = details;
      return (
        <SupercontractForm
          contractId={contractId}
          isReview={isReview}
          isDraft={isDraft}
          contractType={contractType}
        />
      );
    }
    case FlyoutType.PROJECT_SELECT: {
      const { contractType } = details ?? {};
      return <ProjectSelectForm contractType={contractType} />;
    }
    case FlyoutType.PROJECT_BUDGET: {
      const { version } = details ?? {};
      return version ? <ProjectBudget version={version} /> : <EditProjectBudgetForm />;
    }
    case FlyoutType.PROJECT_DRAWN_TO_DATE: {
      const { version } = details ?? {};
      return version ? <ProjectDrawnToDate version={version} /> : <EditProjectBudgetForm />;
    }
    case FlyoutType.PROJECT_COST_TO_COMPLETE: {
      const { version } = details ?? {};
      return version ? <ProjectCostToComplete version={version} /> : <EditProjectBudgetForm />;
    }
    case FlyoutType.PROJECT_DRAWDOWNS: {
      const { version } = details ?? {};
      return version ? <ProjectDrawdowns version={version} /> : <EditProjectBudgetForm />;
    }
    case FlyoutType.PROJECT_LOAN_ADJUSTMENTS: {
      const { version } = details ?? {};
      return version ? <ProjectLoanAdjustments version={version} /> : <EditProjectBudgetForm />;
    }
    case FlyoutType.EDIT_PROJECT_BUDGET: {
      const { version } = details ?? {};
      return <EditProjectBudgetForm version={version} />;
    }
    case FlyoutType.SUBMIT_CLAIM: {
      return <SubmitClaimForm />;
    }
    case FlyoutType.PAYMENT_REQUEST_DETAILS: {
      const { requestId } = details ?? {};
      return <PaymentRequestDetails requestId={requestId} />;
    }
    case FlyoutType.SUPER_CLAIM: {
      const { action, paymentId, claimId, type } = details;
      return <SuperclaimForm action={action} paymentId={paymentId} claimId={claimId} type={type} />;
    }
    case FlyoutType.JOIN_CONTRACT: {
      const { contractId, partyId } = details;
      return <JoinContractForm contractId={contractId} partyId={partyId} />;
    }
    case FlyoutType.UPLOAD_SIGNED_CONTRACT: {
      const { contractId } = details;
      return <UploadSignedContractForm contractId={contractId} />;
    }
    case FlyoutType.CHANGE_PRIMARY_CONTACT: {
      const { partyId } = details;
      return <ChangePrimaryContactForm partyId={partyId} />;
    }
    case FlyoutType.UPDATE_FEE_STRUCTURE: {
      const { contractId } = details;
      return <UpdateFeeStructureForm contractId={contractId} />;
    }
    case FlyoutType.INVITE_BY_EMAIL: {
      const { partyId } = details;
      return <InviteByEmailForm partyId={partyId} />;
    }
    case FlyoutType.INVITE_BY_LINK: {
      const { partyId, userContractInfoId, linkType } = details;
      return (
        <InviteByLinkForm
          partyId={partyId}
          userContractInfoId={userContractInfoId}
          linkType={linkType}
        />
      );
    }
    case FlyoutType.REVIEW_CONTRACT: {
      return <PrimeContractForm actionType={ContractActionType.REVIEW} />;
    }
    case FlyoutType.CONTRACT_DETAILS: {
      const { contractId, subTab = 'overview' } = details ?? {};
      return <ContractDetails contractId={contractId} defaultTab={subTab} />;
    }
    case FlyoutType.SIGN_CONTRACT: {
      const { contractId } = details ?? {};
      return <SignContract contractId={contractId} />;
    }
    case FlyoutType.CONTRACT_NAVIGATOR: {
      const { contractId } = details ?? {};
      return <ContractNavigator contractId={contractId} />;
    }
    case FlyoutType.ACCOUNT_DETAILS: {
      const { contractId, subTab = 'overview' } = details ?? {};
      return <AccountDetails contractId={contractId} defaultTab={subTab} />;
    }
    case FlyoutType.DEPOSIT_REFUND: {
      const { contractId, requestId } = details ?? {};
      return <DepositRefundForm contractId={contractId} requestId={requestId} />;
    }
    case FlyoutType.CONTRACT_TERMINATE: {
      const { contractId, requestId } = details ?? {};
      return <TerminateContractForm contractId={contractId} requestId={requestId} />;
    }
    case FlyoutType.UPDATE_CLAIM: {
      const { claimId } = details ?? {};
      return <UpdateConsolidatedClaimForm claimId={claimId} />;
    }
    case FlyoutType.EARLY_RELEASE: {
      const { claimId, isReview } = details ?? {};
      return <EarlyReleaseForm claimId={claimId} isReview={isReview} />;
    }
    case FlyoutType.PAY_CLAIM: {
      const { claimId } = details ?? {};
      return <PayClaimForm claimId={claimId} />;
    }
    case FlyoutType.LINKED_PAYMENTS: {
      const { paymentId } = details ?? {};
      return <LinkedPayments paymentId={paymentId} />;
    }
    case FlyoutType.PAYMENT_DETAILS: {
      const { paymentId, subTab = 'overview' } = details ?? {};
      return <PaymentDetails paymentId={paymentId} defaultTab={subTab} />;
    }
    case FlyoutType.CLAIM_DETAILS: {
      const { claimId, subTab = 'overview' } = details ?? {};
      return <ClaimDetails claimId={claimId} defaultTab={subTab} />;
    }
    case FlyoutType.NEW_VARIATION: {
      return <PeriodicVariationForm actionType={VariationActionType.CREATE} />;
    }
    case FlyoutType.AMEND_VARIATION: {
      const { variationId } = details ?? {};
      return (
        <PeriodicVariationForm actionType={VariationActionType.AMEND} variationId={variationId} />
      );
    }
    case FlyoutType.REVIEW_VARIATION: {
      const { variationId } = details ?? {};
      return (
        <PeriodicVariationForm actionType={VariationActionType.REVIEW} variationId={variationId} />
      );
    }
    case FlyoutType.VARIATION_DETAILS: {
      const { variationId, subTab = 'overview' } = details ?? {};
      return <VariationDetails variationId={variationId} defaultTab={subTab} />;
    }
    case FlyoutType.ADJUSTMENT_DETAILS: {
      const { adjustmentId, subTab = 'overview' } = details ?? {};
      return <AdjustmentDetails adjustmentId={adjustmentId} defaultTab={subTab} />;
    }
    case FlyoutType.SECURE_PAYMENT: {
      const { paymentId, claimId } = details ?? {};
      return <SecurePayment paymentId={paymentId} claimId={claimId} />;
    }
    case FlyoutType.SECURE_VARIATION: {
      const { variationId } = details ?? {};
      return <SecureVariation variationId={variationId} />;
    }
    case FlyoutType.ASSIGN_FUNDS: {
      const { matcherEntryNumber, subTab } = details ?? {};
      return <AssignHeldFundsForm matcherEntryNumber={matcherEntryNumber} defaultTab={subTab} />;
    }
    case FlyoutType.SECURE_PAID_PRELOAD: {
      const { contractId, requestId, secureContract } = details ?? {};
      return (
        <SecurePaid contractId={contractId} requestId={requestId} secureContract={secureContract} />
      );
    }
    case FlyoutType.UNSECURE_PAID_PRELOAD: {
      const { contractId, requestId } = details ?? {};
      return <UnsecurePaid contractId={contractId} requestId={requestId} />;
    }
    case FlyoutType.ACCESS_LINKED_CONTRACTS: {
      const { contractId, requestId } = details ?? {};
      return <AccessLinkedContractsRequestForm contractId={contractId} requestId={requestId} />;
    }
    case FlyoutType.ABANDON_PAYMENT_REQUEST: {
      const { requestId } = details ?? {};
      return <AbandonPaymentRequestForm requestId={requestId} />;
    }
    case FlyoutType.REQUEST_ASSESSMENT: {
      const { contractId, claimId, requestId } = details ?? {};
      return (
        <RequestAssessmentForm contractId={contractId} claimId={claimId} requestId={requestId} />
      );
    }
    case FlyoutType.CLAIM_ASSESSMENT: {
      const { contractId, claimId, requestId } = details ?? {};
      return (
        <ClaimAssessmentForm contractId={contractId} claimId={claimId} requestId={requestId} />
      );
    }
    case FlyoutType.CHECKOUT: {
      const {
        product,
        payload,
        submitText,
        submittingText,
        isSubmitting,
        onSessionReady,
        onCheckoutFail,
        onCheckoutSuccess,
      } = details ?? {};
      return (
        <CheckoutForm
          product={product}
          payload={payload}
          submitText={submitText}
          submittingText={submittingText}
          isSubmitting={isSubmitting}
          onSessionReady={onSessionReady}
          onCheckoutFail={onCheckoutFail}
          onCheckoutSuccess={onCheckoutSuccess}
        />
      );
    }
    case FlyoutType.FORMS: {
      return <ContractForms />;
    }
    case FlyoutType.PROJECT_FORMS: {
      return <ProjectForms />;
    }
    case FlyoutType.RECALL_CLAIM: {
      const { contractId, claimId, claimType, isApproved } = details ?? {};
      return (
        <RecallClaimForm
          contractId={contractId}
          claimId={claimId}
          claimType={claimType}
          isApproved={isApproved}
        />
      );
    }
    case FlyoutType.UPDATE_CLAIM_AMOUNT: {
      const { contractId, claimId } = details ?? {};
      return <UpdateClaimAmountForm contractId={contractId} claimId={claimId} />;
    }
    case FlyoutType.SUBMIT_RETENTION_RETURN: {
      const { requestId } = details ?? {};
      return <SubmitRetentionReturnForm requestId={requestId} />;
    }
    case FlyoutType.REVIEW_RETENTION_RETURN: {
      const { requestId } = details ?? {};
      return <ReviewRetentionReturnForm requestId={requestId} />;
    }
    case FlyoutType.RETENTION_RETURN_HISTORY: {
      return <RetentionReturnHistory />;
    }
    case FlyoutType.RETENTION_RETURN_DETAILS: {
      const { requestId } = details ?? {};
      return <RetentionReturnDetails requestId={requestId} />;
    }
    case FlyoutType.CONTRACT_REQUIRED_ACTIONS: {
      return <ContractRequiredActionsFlyout />;
    }
    case FlyoutType.CONTRACT_QS_REPORTS: {
      return <ContractQSReportsFlyout />;
    }
    case FlyoutType.CONTRACT_DOCUMENTS: {
      return <ContractDocumentsFlyout />;
    }
    case FlyoutType.CONTRACT_PAYMENTS: {
      return <ContractPaymentsFlyout />;
    }
    case FlyoutType.CONTRACT_VARIATIONS: {
      return <ContractVariationsFlyout />;
    }
    case FlyoutType.CONTRACT_RETENTION: {
      return <ContractRetentionFlyout />;
    }
    default: {
      return <DefaultGlobalFlyoutContent type={type} />;
    }
  }
};

export const GlobalFlyout: React.FC = () => {
  const {
    open,
    nestedOpen,
    type,
    details,
    showCloseIcon,
    closeOnClickOutside,
    closeOnEscapeKeyDown,
    className,
    expandOnNested,
    closeCallback,
  } = useSnapshot(flyoutManager);
  const router = useRouter();
  const searchParams = useSearchParams();
  const dialog = searchParams.get('dialog');
  const mountedRef = useRef(false);

  const handleFlyoutClose = () => {
    dismissFlyout();
    setTimeout(closeCallback, 150);
  };

  // ESC handling. Radix fires this first; if we don't call preventDefault, it
  // proceeds to onOpenChange (handleFlyoutClose) and closes the drawer.
  // Three cases to reconcile:
  //   1. A flyout registered `escapeKeyDownHandler` — it owns a bespoke close
  //      flow (e.g. supercontract's unsaved-draft prompt). Block Radix's
  //      default close and delegate to it so ESC matches the close-icon button.
  //   2. No handler, `closeOnEscapeKeyDown === false` — block ESC entirely.
  //   3. No handler, `closeOnEscapeKeyDown === true` — let Radix close the drawer.
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    const escapeKeyDownHandler = getFlyoutEscapeKeyDownHandler();
    if (escapeKeyDownHandler) {
      event.preventDefault();
      escapeKeyDownHandler();
      return;
    }
    if (!closeOnEscapeKeyDown) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    switch (dialog) {
      case 'edit-personal': {
        openFlyout(FlyoutType.EDIT_PERSONAL_INFO, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'edit-business': {
        openFlyout(FlyoutType.EDIT_BUSINESS_INFO, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'edit-trustees': {
        openFlyout(FlyoutType.EDIT_TRUSTEES_INFO, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'edit-individual-trustees': {
        openFlyout(FlyoutType.EDIT_INDIVIDUAL_TRUSTEES, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'edit-signatory': {
        const relation = router.query.relation;
        openFlyout(FlyoutType.EDIT_SIGNATORY, {
          className: 'w-[752px]',
          details: {
            relation,
          },
        });
        break;
      }
      case 'invite-user': {
        const userGroupId = router.query.userGroupId;
        if (!userGroupId) return;
        openFlyout(FlyoutType.INVITE_USER, {
          className: 'w-[752px]',
          details: {
            userGroupId,
          },
        });
        break;
      }
      case 'edit-director-structure': {
        const userGroupId = router.query.userGroupId;
        const businessId = router.query.businessId;
        if (!userGroupId) return;
        openFlyout(FlyoutType.EDIT_DIRECTOR_STRUCTURE, {
          className: 'w-[752px]',
          details: {
            userGroupId,
            businessId,
          },
        });
        break;
      }
      case 'set-default-signatories': {
        const userGroupId = router.query.userGroupId;
        if (!userGroupId) return;
        openFlyout(FlyoutType.SET_DEFAULT_SIGNATORIES, {
          className: 'w-[752px]',
          details: {
            userGroupId,
          },
        });
        break;
      }
      case EDIT_PARTY_SIGNATORIES_DIALOG: {
        const contractId = router.query.contractId;
        const partyId = router.query.partyId;
        if (!contractId || !partyId) return;
        openFlyout(FlyoutType.EDIT_PARTY_SIGNATORIES, {
          className: 'w-[752px]',
          details: {
            contractId,
            partyId,
          },
        });
        break;
      }
      case 'apply-account': {
        openFlyout(FlyoutType.APPLY_ACCOUNT);
        break;
      }
      case 'payment-account': {
        openFlyout(FlyoutType.PAYMENT_ACCOUNT, { className: 'w-[752px]' });
        break;
      }
      case 'kyc-only': {
        openFlyout(FlyoutType.KYC_ONLY);
        break;
      }
      case 'bank-accounts': {
        openFlyout(FlyoutType.MANAGE_PAYOUT_ACCOUNT, {
          className: 'w-[752px]',
          // The mobile and identity-check detours are nested flyouts of the same width, so
          // expanding for them would move every step of this flow to the far left and back
          // for a panel that covers it regardless.
          expandOnNested: false,
          details: {
            externalBankAccountId: router.query.externalBankAccountId,
          },
        });
        break;
      }
      case 'transaction-details': {
        const matcherEntryNumber = router.query.matcherEntryNumber;
        if (!matcherEntryNumber) return;
        openFlyout(FlyoutType.TRANSACTION_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            matcherEntryNumber,
            subTab: router.query.subTab,
            version: router.query.version,
          },
        });
        break;
      }
      case 'transaction-history': {
        const { accountId } = router.query;
        if (!accountId) return;
        openFlyout(FlyoutType.TRANSACTION_HISTORY, {
          className: 'w-[calc(100vw-80px)] max-w-none',
          closeOnClickOutside: false,
          details: { accountId },
        });
        break;
      }
      case 'funding-details': {
        openFlyout(FlyoutType.FUNDING_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
        });
        break;
      }
      case 'new-project': {
        openFlyout(FlyoutType.CREATE_PROJECT);
        break;
      }
      case 'edit-project': {
        openFlyout(FlyoutType.EDIT_PROJECT);
        break;
      }
      case 'project-select': {
        const contractType = searchParams.getAll('contractType');
        openFlyout(FlyoutType.PROJECT_SELECT, {
          details: {
            contractType,
          },
        });
        break;
      }
      case 'project-budget': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.PROJECT_BUDGET, {
          details: {
            version,
          },
        });
        break;
      }
      case 'project-drawn-to-date': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.PROJECT_DRAWN_TO_DATE, {
          details: {
            version,
          },
        });
        break;
      }
      case 'project-cost-to-complete': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.PROJECT_COST_TO_COMPLETE, {
          details: {
            version,
          },
        });
        break;
      }
      case 'project-drawdowns': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.PROJECT_DRAWDOWNS, {
          details: {
            version,
          },
        });
        break;
      }
      case 'project-loan-adjustments': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.PROJECT_LOAN_ADJUSTMENTS, {
          details: {
            version,
          },
        });
        break;
      }
      case 'edit-project-budget': {
        const version = searchParams.get('version');
        openFlyout(FlyoutType.EDIT_PROJECT_BUDGET, {
          details: {
            version,
          },
        });
        break;
      }
      case 'complete-contract':
      case 'join-contract': {
        const contractId = searchParams.get('contractId');
        const partyId = searchParams.get('partyId');
        if (!contractId || !partyId) return;

        openFlyout(FlyoutType.JOIN_CONTRACT, {
          details: {
            contractId,
            partyId,
          },
        });
        break;
      }
      case 'new-super-contract': {
        const contractType = searchParams.getAll('contractType');
        const isDraft = router.query.draft === '1';
        openFlyout(FlyoutType.SUPER_CONTRACT, {
          className: 'w-[calc(100vw-80px)]',
          closeOnClickOutside: false,
          closeOnEscapeKeyDown: false,
          details: {
            contractType,
            contractId: isDraft ? router.query.contractId : '',
            isDraft: router.query.draft === '1',
          },
        });
        break;
      }
      case 'unshared-drafts': {
        openFlyout(FlyoutType.UNSHARED_DRAFTS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'project-parties': {
        openFlyout(FlyoutType.PROJECT_PARTIES, {
          className: 'w-[1440px]',
        });
        break;
      }
      case 'project-contracts': {
        openFlyout(FlyoutType.PROJECT_CONTRACTS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'review-super-contract': {
        openFlyout(FlyoutType.SUPER_CONTRACT, {
          className: 'w-[calc(100vw-80px)]',
          closeOnClickOutside: false,
          closeOnEscapeKeyDown: false,
          details: {
            contractId: router.query.contractId,
            isDraft: router.query.draft === '1',
            isReview: true,
          },
        });
        break;
      }
      case 'review-contract': {
        if (upper(router.query.template) === ContractTemplate.SUPER_CONTRACT) {
          openFlyout(FlyoutType.SUPER_CONTRACT, {
            closeOnClickOutside: false,
            closeOnEscapeKeyDown: false,
            details: {
              contractId: router.query.contractId,
              isDraft: router.query.draft === '1',
              isReview: true,
            },
          });
          return;
        }

        if (!upper(router.query.template).includes('RBN')) {
          return;
        }

        openFlyout(FlyoutType.REVIEW_CONTRACT);
        break;
      }
      case 'sign-contract': {
        const contractId = router.query.contractId || router.query.contract;
        const contractTemplate = router.query.contractTemplate;
        if (!contractId) return;
        if (contractTemplate !== ContractTemplate.SUPER_CONTRACT) return;
        openFlyout(FlyoutType.SIGN_CONTRACT, {
          details: {
            contractId,
          },
        });
        break;
      }
      case 'contract-filters': {
        openFlyout(FlyoutType.CONTRACT_FILTERS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'claim-filters': {
        openFlyout(FlyoutType.CLAIM_FILTERS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'project-filters': {
        openFlyout(FlyoutType.PROJECT_FILTERS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'invite-by-email':
      case 'reinvite-superintendent':
      case 'reinvite-architect':
      case 'resend-invite': {
        const partyId = router.query.partyId;
        if (!partyId) return;
        openFlyout(FlyoutType.INVITE_BY_EMAIL, {
          className: 'w-[752px]',
          showCloseIcon: true,
          details: {
            partyId,
          },
        });
        break;
      }
      case 'invite-by-link': {
        const partyId = router.query.partyId;
        if (!partyId) return;
        openFlyout(FlyoutType.INVITE_BY_LINK, {
          className: 'w-[752px]',
          details: {
            partyId,
            userContractInfoId: router.query.userContractInfoId,
            linkType: router.query.linkType,
          },
        });
        break;
      }
      case 'invite-funder': {
        openFlyout(FlyoutType.INVITE_PARTY, {
          className: 'w-[752px]',
          details: {
            partyId: router.query.partyId,
            role: ContractRole.SPONSOR,
          },
        });
        break;
      }
      case 'invite-qs': {
        openFlyout(FlyoutType.INVITE_PARTY, {
          className: 'w-[752px]',
          details: {
            partyId: router.query.partyId,
            role: ContractRole.QUANTITY_SURVEYOR,
          },
        });
        break;
      }
      case 'access-linked-contracts': {
        const contractId = router.query.contractId || router.query.contract;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.ACCESS_LINKED_CONTRACTS, {
          className: 'w-[752px]',
          details: {
            contractId,
            requestId,
          },
        });
        break;
      }
      case 'contract-details': {
        const contractId = router.query.contractId || router.query.contract;
        if (!contractId) return;
        openFlyout(FlyoutType.CONTRACT_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            contractId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'contract-navigator': {
        const contractId = router.query.contractId || router.query.contract;
        if (!contractId) return;
        openFlyout(FlyoutType.CONTRACT_NAVIGATOR, {
          details: {
            contractId,
          },
        });
        break;
      }
      case 'account-details': {
        const contractId = router.query.contractId || router.query.contract;
        if (!contractId) return;
        openFlyout(FlyoutType.ACCOUNT_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            contractId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'deposit-refund': {
        const contractId = router.query.contractId || router.query.contract;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.DEPOSIT_REFUND, {
          className: 'w-[752px]',
          details: {
            contractId,
            requestId,
          },
        });
        break;
      }
      case 'submit-retention-return': {
        const requestId = router.query.requestId || router.query.request;
        openFlyout(FlyoutType.SUBMIT_RETENTION_RETURN, {
          className: 'w-[752px]',
          details: {
            requestId,
          },
        });
        break;
      }
      case 'review-retention-return': {
        const requestId = router.query.requestId || router.query.request;
        if (!requestId) return;
        openFlyout(FlyoutType.REVIEW_RETENTION_RETURN, {
          className: 'w-[752px]',
          details: {
            requestId,
          },
        });
        break;
      }
      case 'retention-return-history': {
        openFlyout(FlyoutType.RETENTION_RETURN_HISTORY, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'retention-return-details': {
        const requestId = router.query.requestId || router.query.request;
        if (!requestId) return;
        openFlyout(FlyoutType.RETENTION_RETURN_DETAILS, {
          className: 'w-[752px]',
          details: {
            requestId,
          },
        });
        break;
      }
      case 'terminate-contract': {
        const contractId = router.query.contractId || router.query.contract;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.CONTRACT_TERMINATE, {
          className: 'w-[752px]',
          details: {
            contractId,
            requestId,
          },
        });
        break;
      }
      case 'new-claim': {
        const paymentId = router.query.paymentId || router.query.payment;
        const claimId = router.query.claimId || router.query.claim;
        openFlyout(FlyoutType.SUPER_CLAIM, {
          closeOnClickOutside: false,
          details: {
            action: ClaimAction.CREATE,
            paymentId,
            claimId,
            type: router.query.type || ClaimType.STANDARD,
          },
        });
        break;
      }
      case 'amend-claim': {
        const claimId = router.query.claimId || router.query.claim;
        openFlyout(FlyoutType.SUPER_CLAIM, {
          closeOnClickOutside: false,
          details: {
            action: ClaimAction.AMEND,
            claimId,
            type: router.query.type || ClaimType.STANDARD,
          },
        });
        break;
      }
      case 'review-claim': {
        const claimId = router.query.claimId || router.query.claim;
        openFlyout(FlyoutType.SUPER_CLAIM, {
          closeOnClickOutside: false,
          details: {
            action: ClaimAction.REVIEW,
            claimId,
            type: router.query.type || ClaimType.STANDARD,
          },
        });
        break;
      }
      case 'update-claim': {
        const claimId = router.query.claimId || router.query.claim;
        if (!claimId) return;
        openFlyout(FlyoutType.UPDATE_CLAIM, {
          className: 'w-[752px]',
          details: {
            claimId,
          },
        });
        break;
      }
      case 'earlyrelease':
      case 'get-insta-paid': {
        const claimId = router.query.claimId || router.query.claim;
        if (!claimId) return;
        openFlyout(FlyoutType.EARLY_RELEASE, {
          className: 'w-[752px]',
          details: {
            claimId,
          },
        });
        break;
      }
      case 'earlyrelease-review-offer': {
        const claimId = router.query.claimId || router.query.claim;
        if (!claimId) return;
        openFlyout(FlyoutType.EARLY_RELEASE, {
          className: 'w-[752px]',
          details: {
            isReview: true,
            claimId,
          },
        });
        break;
      }
      case 'pay-claim': {
        const claimId = router.query.claimId || router.query.claim;
        if (!claimId) return;
        openFlyout(FlyoutType.PAY_CLAIM, {
          className: 'w-[752px]',
          details: {
            claimId,
          },
        });
        break;
      }
      case 'linked-payments': {
        const paymentId = router.query.paymentId || router.query.payment;
        if (!paymentId) return;
        openFlyout(FlyoutType.LINKED_PAYMENTS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            paymentId,
          },
        });
        break;
      }
      case 'payment-details': {
        const paymentId = router.query.paymentId || router.query.payment;
        if (!paymentId) return;
        openFlyout(FlyoutType.PAYMENT_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            paymentId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'claim-details': {
        const claimId = router.query.claimId || router.query.claim;
        if (!claimId) return;
        openFlyout(FlyoutType.CLAIM_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            claimId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'work-item-details': {
        const workItemId = router.query.workItemId || router.query.workItem;
        if (!workItemId) return;
        openFlyout(FlyoutType.WORK_ITEM_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            workItemId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'payment-request-details': {
        const requestId = router.query.requestId;
        if (!requestId) return;
        openFlyout(FlyoutType.PAYMENT_REQUEST_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            requestId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'abandon-payment-request': {
        const requestId = router.query.requestId;
        if (!requestId) return;
        openFlyout(FlyoutType.ABANDON_PAYMENT_REQUEST, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            requestId,
          },
        });
        break;
      }
      case 'new-variation': {
        const claimMethod = upper(router.query.method);
        if (claimMethod !== ClaimMethod.PERIODIC) return;
        openFlyout(FlyoutType.NEW_VARIATION, {
          details: {
            claimMethod,
          },
        });
        break;
      }
      case 'amend-variation': {
        const claimMethod = upper(router.query.method);
        if (claimMethod !== ClaimMethod.PERIODIC) {
          closeFlyout();
          return;
        }
        const variationId = router.query.variationId || router.query.variation;
        if (!variationId) return;
        openFlyout(FlyoutType.AMEND_VARIATION, {
          details: {
            claimMethod,
            variationId,
          },
        });
        break;
      }
      case 'review-variation': {
        const claimMethod = upper(router.query.method);
        if (claimMethod !== ClaimMethod.PERIODIC) {
          closeFlyout();
          return;
        }
        const variationId = router.query.variationId || router.query.variation;
        if (!variationId) return;
        openFlyout(FlyoutType.REVIEW_VARIATION, {
          details: {
            claimMethod,
            variationId,
          },
        });
        break;
      }
      case 'variation-details': {
        const variationId = router.query.variationId || router.query.variation;
        if (!variationId) return;
        openFlyout(FlyoutType.VARIATION_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            variationId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'adjustment-details': {
        const adjustmentId = router.query.adjustmentId || router.query.adjustment;
        if (!adjustmentId) return;
        openFlyout(FlyoutType.ADJUSTMENT_DETAILS, {
          className: 'w-[752px]',
          closeOnClickOutside: true,
          details: {
            adjustmentId,
            subTab: router.query.subTab,
          },
        });
        break;
      }
      case 'help-center': {
        openFlyout(FlyoutType.HELP_CENTER, {
          className: 'w-[752px]',
          showCloseIcon: true,
          closeOnClickOutside: true,
        });
        break;
      }
      case 'account-transfer': {
        openFlyout(FlyoutType.ACCOUNT_TRANSFER, {
          showCloseIcon: false,
          className: 'w-[752px] flex-unset',
        });
        break;
      }
      case 'account-transfer-intra': {
        openFlyout(FlyoutType.ACCOUNT_TRANSFER_INTRA, {
          showCloseIcon: false,
          className: 'w-[752px] flex-unset',
        });
        break;
      }
      case 'secure-payment': {
        const claimId = router.query.claimId || router.query.claim;
        const paymentId = router.query.paymentId || router.query.payment;
        if (!paymentId) return;
        openFlyout(FlyoutType.SECURE_PAYMENT, {
          className: 'w-[752px]',
          details: {
            paymentId,
            claimId,
          },
        });
        break;
      }
      case 'secure-variation': {
        const variationId = router.query.variationId || router.query.variation;
        if (!variationId) return;
        openFlyout(FlyoutType.SECURE_VARIATION, {
          className: 'w-[752px]',
          details: {
            variationId,
          },
        });
        break;
      }
      case 'secure-paid': {
        const contractId = router.query.contractId || router.query.contract;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.SECURE_PAID_PRELOAD, {
          className: 'w-[752px]',
          details: {
            contractId,
            requestId,
          },
        });
        break;
      }
      case 'assign-funds': {
        const matcherEntryNumber = router.query.matcherEntryNumber;
        if (!matcherEntryNumber) return;
        openFlyout(FlyoutType.ASSIGN_FUNDS, {
          className: 'w-[752px]',
          details: {
            matcherEntryNumber,
            version: router.query.version,
          },
        });
        break;
      }
      case 'secure-contract': {
        const contractId = router.query.contractId || router.query.contract;
        if (!contractId) return;
        openFlyout(FlyoutType.SECURE_PAID_PRELOAD, {
          className: 'w-[752px]',
          details: {
            contractId,
            secureContract: true,
          },
        });
        break;
      }
      case 'unsecure-paid': {
        const contractId = router.query.contractId || router.query.contract;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.UNSECURE_PAID_PRELOAD, {
          className: 'w-[752px]',
          details: {
            contractId,
            requestId,
          },
        });
        break;
      }
      case 'request-assessment': {
        const contractId = router.query.contractId || router.query.contract;
        const claimId = router.query.claimId || router.query.claim;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.REQUEST_ASSESSMENT, {
          className: 'w-[752px]',
          details: {
            contractId,
            claimId,
            requestId,
          },
        });
        break;
      }
      case 'claim-assessment': {
        const contractId = router.query.contractId || router.query.contract;
        const claimId = router.query.claimId || router.query.claim;
        const requestId = router.query.requestId || router.query.request;
        if (!contractId) return;
        openFlyout(FlyoutType.CLAIM_ASSESSMENT, {
          className: 'w-[752px]',
          details: {
            contractId,
            claimId,
            requestId,
          },
        });
        break;
      }
      case 'abandon-contract': {
        openFlyout(FlyoutType.ABANDON_CONTRACT, {
          className: 'w-[752px]',
          showCloseIcon: true,
        });
        break;
      }
      case 'delete-draft-contract': {
        openFlyout(FlyoutType.DELETE_DRAFT_CONTRACT, {
          className: 'w-[752px]',
          showCloseIcon: true,
        });
        break;
      }
      case 'revoke-contract': {
        const contractId = router.query.contractId;
        if (!contractId) return;
        openFlyout(FlyoutType.REVOKE_CONTRACT, {
          className: 'w-[752px]',
          details: {
            contractId: router.query.contractId,
          },
        });
        break;
      }
      case 'recall-contract': {
        const contractId = router.query.contractId;
        if (!contractId) return;
        openFlyout(FlyoutType.RECALL_CONTRACT, {
          className: 'w-[752px]',
          details: {
            contractId: router.query.contractId,
          },
        });
        break;
      }
      case 'retire-user-group': {
        const userGroupId = router.query.userGroupId;
        if (!userGroupId) return;
        openFlyout(FlyoutType.RETIRE_USER_GROUP, {
          className: 'w-[752px]',
          details: {
            userGroupId,
          },
        });
        break;
      }
      case 'unjoin-contract': {
        const contractId = router.query.contractId;
        if (!contractId) return;
        openFlyout(FlyoutType.UNJOIN_CONTRACT, {
          className: 'w-[752px]',
          details: {
            contractId,
          },
        });
        break;
      }
      case 'upload-signed-contract': {
        const contractId = router.query.contractId;
        if (!contractId) return;
        openFlyout(FlyoutType.UPLOAD_SIGNED_CONTRACT, {
          className: 'w-[752px]',
          details: {
            contractId,
          },
        });
        break;
      }
      case 'change-primary-contact': {
        const contractId = router.query.contractId;
        const partyId = router.query.partyId;
        if (!contractId) return;
        openFlyout(FlyoutType.CHANGE_PRIMARY_CONTACT, {
          className: 'w-[752px]',
          details: {
            contractId,
            partyId,
          },
        });
        break;
      }
      case 'update-fee-structure': {
        const contractId = router.query.contractId;
        if (!contractId) return;
        openFlyout(FlyoutType.UPDATE_FEE_STRUCTURE, {
          className: 'w-[752px]',
          details: {
            contractId,
          },
        });
        break;
      }
      case 'set-commencement-date': {
        openFlyout(FlyoutType.SET_COMMENCEMENT_DATE, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'set-completion-date': {
        openFlyout(FlyoutType.SET_COMPLETION_DATE, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'timeline-details': {
        openFlyout(FlyoutType.TIMELINE_DETAILS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'new-linking': {
        const paymentId = router.query.paymentId || router.query.payment;
        if (!paymentId) return;
        openFlyout(FlyoutType.NEW_LINKING, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'contract-activity': {
        openFlyout(FlyoutType.CONTRACT_REQUIRED_ACTIONS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'contract-qs-reports': {
        openFlyout(FlyoutType.CONTRACT_QS_REPORTS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'contract-documents': {
        openFlyout(FlyoutType.CONTRACT_DOCUMENTS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'contract-payments': {
        openFlyout(FlyoutType.CONTRACT_PAYMENTS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'contract-variations': {
        openFlyout(FlyoutType.CONTRACT_VARIATIONS, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'contract-retention': {
        openFlyout(FlyoutType.CONTRACT_RETENTION, {
          className: 'w-[calc(100vw-80px)]',
        });
        break;
      }
      case 'forms': {
        openFlyout(FlyoutType.FORMS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'project-forms': {
        openFlyout(FlyoutType.PROJECT_FORMS, {
          className: 'w-[752px]',
        });
        break;
      }
      case 'recall-claim': {
        const contractId = router.query.contractId;
        const claimId = router.query.claimId;
        const rawClaimType = router.query.claimType;
        const claimTypeValue = Array.isArray(rawClaimType) ? rawClaimType[0] : rawClaimType;
        const claimType =
          claimTypeValue && (Object.values(ClaimType) as string[]).includes(claimTypeValue)
            ? (claimTypeValue as ClaimType)
            : undefined;
        if (!contractId || !claimId) return;
        openFlyout(FlyoutType.RECALL_CLAIM, {
          className: 'w-[752px]',
          details: {
            contractId,
            claimId,
            claimType,
            isApproved: router.query.isApproved === 'true',
          },
        });
        break;
      }
      case 'update-claim-amount': {
        const contractId = router.query.contractId;
        const claimId = router.query.claimId;
        if (!contractId || !claimId) return;
        openFlyout(FlyoutType.UPDATE_CLAIM_AMOUNT, {
          className: 'w-[752px]',
          details: {
            contractId,
            claimId,
          },
        });
        break;
      }
      default: {
        // Skip flyouts that are opened programmatically (no `dialog` query
        // param) so this URL-driven effect doesn't clobber them on mount /
        // shallow router updates.
        if (programmaticOnlyFlyouts.has(flyoutManager.type)) break;
        closeFlyout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dialog,
    router.query.contractId,
    router.query.paymentId,
    router.query.payment,
    router.query.claimId,
    router.query.claim,
  ]);

  useEffect(() => {
    if (open || !mountedRef.current) return;
    const urlSearchParams = new URLSearchParams(globalThis.location.search);

    urlSearchParams.delete('dialog');
    urlSearchParams.delete('t');
    urlSearchParams.delete('action');
    urlSearchParams.delete('step');
    urlSearchParams.delete('subTab');
    urlSearchParams.delete('code');
    urlSearchParams.delete('source');
    urlSearchParams.delete('draft');
    urlSearchParams.delete('readOnly');
    urlSearchParams.delete('relation');
    urlSearchParams.delete('preset');
    urlSearchParams.delete('template');
    urlSearchParams.delete('contractTemplate');
    urlSearchParams.delete('method');
    urlSearchParams.delete('claimMethod');
    urlSearchParams.delete('payment');
    urlSearchParams.delete('paymentId');
    urlSearchParams.delete('requestId');
    urlSearchParams.delete('request');
    urlSearchParams.delete('claim');
    urlSearchParams.delete('claimId');
    urlSearchParams.delete('variation');
    urlSearchParams.delete('variationId');
    urlSearchParams.delete('adjustment');
    urlSearchParams.delete('adjustmentId');
    urlSearchParams.delete('matcherEntryNumber');
    urlSearchParams.delete('provisional');
    urlSearchParams.delete('participantId');
    urlSearchParams.delete('partyId');
    urlSearchParams.delete('partyType');
    urlSearchParams.delete('userId');
    urlSearchParams.delete('userGroupId');
    urlSearchParams.delete('userContractInfoId');
    urlSearchParams.delete('method');
    urlSearchParams.delete('source');
    urlSearchParams.delete('linkType');
    urlSearchParams.delete('type');
    urlSearchParams.delete('version');

    router.replace(
      {
        pathname: globalThis.location.pathname,
        search: urlSearchParams.toString(),
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  return (
    <DrawerRoot open={open} onOpenChange={handleFlyoutClose}>
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerContent
          fullScreen={nestedOpen && expandOnNested}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={closeOnClickOutside ? undefined : preventDefault}
          onInteractOutside={closeOnClickOutside ? undefined : preventDefault}
          className={className}
        >
          <Suspense fallback={<LoadingIndicator />}>
            <DrawerClose hidden={!showCloseIcon} />
            <GlobalFlyoutContent type={type} details={details} />
          </Suspense>
        </DrawerContent>
      </DrawerPortal>
    </DrawerRoot>
  );
};

GlobalFlyout.displayName = 'GlobalFlyout';
